import { Control, Controller, FieldErrors } from 'react-hook-form';
import { Input } from '@/shared/components/ui/input';
import { Label } from '@/shared/components/ui/label';
import { Textarea } from '@/shared/components/ui/textarea';
import { Button } from '@/shared/components/ui/button';
import { X } from 'lucide-react';
import { useState } from 'react';
import type { ProviderEditorFormData } from '../schemas';

type ProfessionalCredentialsSectionProps = {
  control: Control<ProviderEditorFormData>;
  errors: FieldErrors<ProviderEditorFormData>;
};

type FormFieldProps = {
  label: string;
  required?: boolean;
  error?: string;
  htmlFor?: string;
  children: React.ReactNode;
  hint?: string;
};

function FormField({
  label,
  required = false,
  error,
  htmlFor,
  children,
  hint,
}: FormFieldProps) {
  return (
    <div className="space-y-2">
      <Label htmlFor={htmlFor} className="text-sm font-medium text-gray-700">
        {label}
        {required && <span className="ml-1 text-red-500">*</span>}
      </Label>
      {children}
      {hint && <p className="text-xs text-gray-500">{hint}</p>}
      {error && <p className="mt-1 text-sm text-red-600">{error}</p>}
    </div>
  );
}

/**
 * ArrayFieldManager - Reusable component for managing array fields
 */
function ArrayFieldManager({
  label,
  value,
  onChange,
  placeholder,
  hint,
}: {
  label: string;
  value: string[];
  onChange: (newValue: string[]) => void;
  placeholder: string;
  hint?: string;
}) {
  const [inputValue, setInputValue] = useState('');

  const handleAdd = () => {
    const trimmed = inputValue.trim();
    if (trimmed && !value.includes(trimmed)) {
      onChange([...value, trimmed]);
      setInputValue('');
    }
  };

  const handleRemove = (index: number) => {
    onChange(value.filter((_, i) => i !== index));
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAdd();
    }
  };

  return (
    <div className="space-y-3">
      <Label className="text-sm font-medium text-gray-700">{label}</Label>

      {/* Input */}
      <div className="flex gap-2">
        <Input
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="flex-1"
        />
        <Button type="button" variant="outline" size="sm" onClick={handleAdd}>
          Add
        </Button>
      </div>

      {hint && <p className="text-xs text-gray-500">{hint}</p>}

      {/* List */}
      {value.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {value.map((item, index) => (
            <div
              key={index}
              className="inline-flex items-center gap-2 rounded-md bg-blue-50 px-3 py-1 text-sm text-blue-700"
            >
              <span>{item}</span>
              <button
                type="button"
                onClick={() => handleRemove(index)}
                className="text-blue-500 hover:text-blue-700"
              >
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * ProfessionalCredentialsSection - Professional qualifications and credentials
 * 
 * Contains:
 * - Professional title
 * - Years of experience
 * - Certifications (array)
 * - Professional licenses (array)
 * - Awards and recognitions (array)
 * - Professional associations (array)
 * - Continuing education description
 */
export function ProfessionalCredentialsSection({
  control,
  errors,
}: ProfessionalCredentialsSectionProps) {
  return (
    <div className="space-y-6">
      {/* Professional Title */}
      <FormField
        label="Professional Title"
        htmlFor="professional-title"
        error={errors.provider?.professionalTitle?.message}
        hint="Job title or role (max 255 characters)"
      >
        <Controller
          name="provider.professionalTitle"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              value={field.value || ''}
              id="professional-title"
              maxLength={255}
              className={errors.provider?.professionalTitle ? 'border-red-500' : ''}
            />
          )}
        />
      </FormField>

      {/* Years of Experience */}
      <FormField
        label="Years of Experience"
        htmlFor="years-experience"
        error={errors.provider?.yearsExperience?.message}
        hint="Years working in this field"
      >
        <Controller
          name="provider.yearsExperience"
          control={control}
          render={({ field }) => (
            <Input
              {...field}
              value={field.value ?? ''}
              onChange={(e) => {
                const value = e.target.value === '' ? null : parseInt(e.target.value);
                field.onChange(value);
              }}
              id="years-experience"
              type="number"
              min={0}
              className={errors.provider?.yearsExperience ? 'border-red-500' : ''}
            />
          )}
        />
      </FormField>

      {/* Certifications */}
      <Controller
        name="provider.certificationsArray"
        control={control}
        render={({ field }) => (
          <ArrayFieldManager
            label="Certifications"
            value={field.value || []}
            onChange={field.onChange}
            placeholder=""
            hint="Add relevant certifications one at a time"
          />
        )}
      />

      {/* Professional Licenses */}
      <Controller
        name="provider.professionalLicenses"
        control={control}
        render={({ field }) => (
          <ArrayFieldManager
            label="Professional Licenses"
            value={field.value || []}
            onChange={field.onChange}
            placeholder=""
            hint="Add license numbers and types"
          />
        )}
      />

      {/* Awards and Recognitions */}
      <Controller
        name="provider.awardsRecognitions"
        control={control}
        render={({ field }) => (
          <ArrayFieldManager
            label="Awards & Recognitions"
            value={field.value || []}
            onChange={field.onChange}
            placeholder=""
            hint="Add awards and recognitions"
          />
        )}
      />

      {/* Professional Associations */}
      <Controller
        name="provider.professionalAssociations"
        control={control}
        render={({ field }) => (
          <ArrayFieldManager
            label="Professional Associations"
            value={field.value || []}
            onChange={field.onChange}
            placeholder=""
            hint="Add professional memberships and associations"
          />
        )}
      />

      {/* Continuing Education */}
      <FormField
        label="Continuing Education"
        htmlFor="continuing-education"
        error={errors.provider?.continuingEducation?.message}
        hint="Describe ongoing training and education"
      >
        <Controller
          name="provider.continuingEducation"
          control={control}
          render={({ field }) => (
            <Textarea
              {...field}
              value={field.value || ''}
              id="continuing-education"
              rows={4}
              className={errors.provider?.continuingEducation ? 'border-red-500' : ''}
            />
          )}
        />
      </FormField>
    </div>
  );
}

